import type { AnalyticsSection, SlipSize } from '#shared/types';
import { API_BASE } from './api';
import { RESOURCE } from './routes';

/**
 * Endpoints that are not a CRUD resource.
 *
 * `/lab-profile` is a singleton — one row, no list, no `:id` — and `/rate-list`
 * is a derived document. Neither satisfies the identity `RESOURCE` encodes
 * (API path === SPA route base), so neither belongs in it: the profile is
 * edited at `/settings/company` and the rate list is read at `/rate-list`.
 */
/**
 * A path with its query string, encoded. Blank and undefined values are left
 * out, so an optional filter that was not chosen is absent rather than empty.
 */
export function withQuery(path: string, params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) query.set(key, value);
  }
  const encoded = query.toString();
  return encoded ? `${path}?${encoded}` : path;
}

export const ENDPOINT = {
  labProfile: '/lab-profile',
  dbHealth: '/db-health',
  rateList: '/rate-list',
  pricingQuote: '/pricing/quote',
  stockMovements: '/stock/movements',
  stockAdjustments: '/stock/adjustments',
  stockWastage: '/stock/wastage',
  stockReconciliation: '/stock/reconciliation',
  lowStock: '/inventory/low-stock',
  expiring: '/inventory/expiring',
  coverage: '/inventory/coverage',
  /** The names the lab's sheets use for its tests. Not nested under a run — an
   *  alias outlives the import that prompted it. */
  testAliases: '/imports/aliases',
  /** What a requisition built from the low-stock list would contain. */
  requisitionSuggest: '/requisitions/suggest',
  expenseCategories: '/expenses/categories',
  /** Whether this server can send on WhatsApp at all. Admin. */
  whatsappStatus: '/whatsapp/status',
  /** Whether this visit's reports can be sent, and what would go. */
  whatsappPatient: (id: number | string) => `/whatsapp/patients/${id}`,
  /** The lab-wide send log. Admin. */
  reportShares: '/report-shares',
  income: '/analytics/income',
  profitLoss: '/analytics/profit-loss',
  /** Every year the lab has data for. Money withheld server-side for the bench. */
  yearHistory: '/analytics/history',
  /** The analytics filter bar's option lists, staff names included. */
  analyticsFilters: '/analytics/filters',
  dayClose: '/day-close',
  /** The caller's own session so far — what the sign-out dialog confirms. */
  dayCloseSession: '/day-close/session',
  dayCloseSubmissions: '/day-close/submissions',
  dayCloseReview: (id: number) => `/day-close/submissions/${id}/review`,
  /** A payment or refund after registration. */
  patientPayments: (id: number | string) => `/patients/${id}/payments`,
  /** Renders a draft print template against sample data. Returns a PDF. */
  printPreview: '/print/preview',
  /** A role's change log. */
  roleHistory: (id: number | string) => `/roles/${id}/history`,
  /** Copies a role's permissions under a new name. */
  roleClone: (id: number | string) => `/roles/${id}/clone`,
  /** What one staff member may do: role, exceptions, and the sum. */
  userPermissions: (id: number | string) => `/users/${id}/permissions`,
  userPermissionsHistory: (id: number | string) => `/users/${id}/permissions/history`,
  authMe: '/auth/me',
  authSignIn: '/auth/signin',
  authSignOut: '/auth/signout',
  dashboard: '/dashboard',
  dashboardSnapshot: '/dashboard/snapshot',
  search: '/search',
  analyticsSection: (section: AnalyticsSection) => `/analytics/${section}`,
  dbHealthAnalyze: '/db-health/analyze',
  patientLookup: '/patients/lookup',
  patientShares: (id: number | string) => `/patients/${id}/shares`,
  report: (id: number | string) => `/reports/${id}`,
  reportHistory: (id: number | string) => `/reports/${id}/history`,
  importRun: (id: number | string) => `/imports/${id}`,
  importRows: (id: number | string) => `/imports/${id}/rows`,
  importStructure: (id: number | string) => `/imports/${id}/structure`,
  importMapping: (id: number | string) => `/imports/${id}/mapping`,
  importValidate: (id: number | string) => `/imports/${id}/validate`,
  importCommit: (id: number | string) => `/imports/${id}/commit`,
  orderRequestAction: (id: number | string, action: string) => `/order-requests/${id}/${action}`,
  orderRequestRegister: (id: number | string) => `/order-requests/${id}/register`,
  printTemplateDefault: (id: number | string) => `/print-templates/${id}/default`,
  requisitionReceipts: (id: number | string) => `/requisitions/${id}/receipts`,
  requisitionTransition: (id: number | string) => `/requisitions/${id}/transition`,
} as const;

/**
 * The PDF endpoints.
 *
 * Not in `RESOURCE`, because they break its one rule: these paths are API-only
 * and have no SPA route behind them. They return `application/pdf`, so they are
 * used as an `<iframe src>` or an `<a href>` rather than through `api.get`,
 * which is also why they carry the `/api/v1` prefix that every other path here
 * leaves to the fetch client.
 */

export interface PrintJobOptions {
  template_id?: number | undefined;
  report_ids?: readonly number[] | undefined;
  header?: boolean | undefined;
  footer?: boolean | undefined;
  watermark?: boolean | undefined;
  /** Suppress the signatories at the foot of every page. Cannot name one. */
  signatures?: boolean | undefined;
  /** Print previous results beside the current ones. A layout flag. */
  history?: boolean | undefined;
  /**
   * Which earlier visits to print.
   *
   * Data selection, not layout. The server never *fetches* by these ids — it
   * loads the patient's own history and uses them only to filter it, so an id
   * from another patient cannot pull their results onto this sheet.
   */
  history_ids?: readonly number[] | undefined;
  page_size?: string | undefined;
  orientation?: string | undefined;
  download?: boolean | undefined;
}

function printQuery(options: PrintJobOptions): string {
  const params = new URLSearchParams();

  // An undefined flag is omitted rather than sent as `false`: absent means "as
  // the template says", and the print screen leaves them undefined until the
  // operator actually touches one. Sending the effective value instead would
  // pin this job to whatever the template happened to say when the screen
  // loaded.
  if (options.template_id !== undefined) params.set('template_id', String(options.template_id));
  if (options.report_ids?.length) params.set('report_ids', options.report_ids.join(','));
  if (options.header !== undefined) params.set('header', String(options.header));
  if (options.footer !== undefined) params.set('footer', String(options.footer));
  if (options.watermark !== undefined) params.set('watermark', String(options.watermark));
  if (options.signatures !== undefined) params.set('signatures', String(options.signatures));
  if (options.history !== undefined) params.set('history', String(options.history));
  if (options.history_ids?.length) params.set('history_ids', options.history_ids.join(','));
  if (options.page_size) params.set('page_size', options.page_size);
  if (options.orientation) params.set('orientation', options.orientation);
  if (options.download) params.set('download', 'true');

  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * The importer's two downloads.
 *
 * Workbooks, not JSON, so they are used as an `<a href>` and carry the
 * `/api/v1` prefix that every other path here leaves to the fetch client — the
 * same reason `printUrls` does.
 */
export const importUrls = {
  template: (entity: string) => `${API_BASE}${RESOURCE.imports}/templates/${entity}`,
  errors: (id: number | string) => `${API_BASE}${RESOURCE.imports}/${id}/errors`,
} as const;

export const printUrls = {
  patient: (id: number | string, options: PrintJobOptions = {}) =>
    `${API_BASE}/print/patients/${id}${printQuery(options)}`,
  report: (id: number | string, options: PrintJobOptions = {}) =>
    `${API_BASE}/print/reports/${id}${printQuery(options)}`,
  /**
   * One standard test procedure as a PDF.
   *
   * Not a `PrintTemplate`: an SOP has a fixed shape rather than a layout the lab
   * tunes, so it goes through the letterhead helper the rate list uses.
   */
  procedure: (id: number | string, download = false) =>
    `${API_BASE}${RESOURCE.testProcedures}/${id}/print${download ? '?download=true' : ''}`,
  /** The price list as a PDF. `download` makes the browser save it. */
  rateList: (download = false) =>
    `${API_BASE}${ENDPOINT.rateList}/print${download ? '?download=true' : ''}`,
  /**
   * The registration slip.
   *
   * No `template_id`: a slip has a fixed shape. `charges: false` is the copy
   * that travels with the sample to the bench.
   */
  slip: (id: number | string, options: SlipOptions = {}) =>
    `${API_BASE}/print/patients/${id}/slip${slipQuery(options)}`,
} as const;

interface SlipOptions {
  /** Declared by `slipSizeSchema`, so this cannot offer a paper the server refuses. */
  size?: SlipSize | undefined;
  charges?: boolean | undefined;
  download?: boolean | undefined;
}

function slipQuery(options: SlipOptions): string {
  const params = new URLSearchParams();
  if (options.size) params.set('size', options.size);
  if (options.charges === false) params.set('charges', 'false');
  if (options.download) params.set('download', 'true');

  const query = params.toString();
  return query ? `?${query}` : '';
}
